import { Application } from 'pixi.js';
import { PlayerSprite } from './playerSprite';
import { buildPlayerLayers, type PlayerLook } from './buildPlayerLayers';
import type { BodyFrames, EquipFrames } from './playerArt';
import type { SpriteAtlasStore, FramesLoadListener } from './spriteAtlasStore';
import type { HueTable } from './hueTable';
import { configureUoHueBatch } from './uoHueBatch';
import { Direction } from '../sim/direction';

/** Pixel size of the square preview canvas embedded in the splash card. */
const PREVIEW_SIZE = 120;
/**
 * Empty margin (px) kept inside the canvas edges when the sprite is fitted, so the
 * silhouette never touches the frame and hair/feet have a little breathing room.
 */
const PREVIEW_PADDING = 10;
/**
 * Ceiling on the fitted up-scale. The baked human stands ~40px tall; ~2x fills the
 * small card panel legibly without resampling blur (nearest-neighbour art). The fit
 * ({@link fitPreviewTransform}) never up-scales past this, so the everyday character
 * keeps its crisp 2x look; only a would-be-oversized stack is shrunk below it to
 * stay wholly in frame.
 */
const MAX_PREVIEW_SCALE = 2;

/** Step around the eight UO facings, wrapping in either direction. */
export function rotatePreviewDirection(facing: Direction, steps: number): Direction {
  const count = 8;
  return ((facing + steps % count + count) % count) as Direction;
}

/** A local-space bounding box, the shape of Pixi's `getLocalBounds()`. */
export interface PreviewBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fit a sprite's local bounds into a square preview canvas: pick the largest scale
 * at which the **whole** silhouette (head to feet, every worn layer) sits inside the
 * canvas with {@link PREVIEW_PADDING} to spare, then the position that centres those
 * bounds in the canvas. Pure and clock-free so it is unit-tested headlessly.
 *
 * The character's ground point (the sprite's container origin) is **not** the middle
 * of its art - the body is drawn offset above it, and the shared {@link PlayerSprite}
 * even nudges the stack down by a tile half-height so feet rest on a floor diamond in
 * the world. Positioning the origin at a fixed fraction of the canvas (as this once
 * did) therefore pushed the lower body off the bottom edge. Centring on the measured
 * bounds instead makes the framing independent of that anchor, so the full sprite
 * always shows however the art is offset or however tall the outfit is.
 *
 * Up-scaling is clamped to whole steps (and to {@link MAX_PREVIEW_SCALE}) so the
 * nearest-neighbour art stays crisp; only a stack too big to fit even at 1x is scaled
 * fractionally, trading a touch of sharpness for the guarantee that it stays in frame.
 */
export function fitPreviewTransform(
  bounds: PreviewBounds,
  size = PREVIEW_SIZE,
  padding = PREVIEW_PADDING,
  maxScale = MAX_PREVIEW_SCALE,
): { scale: number; x: number; y: number } {
  const avail = Math.max(1, size - padding * 2);
  // A degenerate (empty-texture) stack has no extent to fit; centre it at max scale.
  if (bounds.width <= 0 || bounds.height <= 0) {
    return { scale: maxScale, x: size / 2, y: size / 2 };
  }
  const fit = Math.min(avail / bounds.width, avail / bounds.height);
  // Snap up-scaling to whole steps to keep the pixel art crisp; fall back to the raw
  // fractional fit only when even 1x overflows, so an oversized stack still fits.
  const scale = fit >= 1 ? Math.min(maxScale, Math.floor(fit)) : fit;
  return {
    scale,
    x: size / 2 - (bounds.x + bounds.width / 2) * scale,
    y: size / 2 - (bounds.y + bounds.height / 2) * scale,
  };
}

/**
 * The live look preview in the splash card (Slice 4).
 *
 * It renders the visitor's assembled character in a small Pixi canvas, **reusing
 * the real {@link PlayerSprite} layer stack and the shared {@link buildPlayerLayers}
 * assembly** the live game uses - so the preview can't lie about the result: the
 * same body art, the same per-layer dye filters, the same facing-dependent draw
 * order and gender gating. It holds the south-facing stand pose (the spawn pose),
 * driven by the same render clock as in-world sprites so the idle fidget plays, and
 * is rebuilt on every selection change via {@link update}.
 *
 * It owns its **own** tiny {@link Application}, independent of the game's, because it
 * lives in the splash DOM before the game renderer exists; {@link destroy} tears it
 * down when the visitor enters.
 */
export class SplashPreview {
  private readonly app = new Application();
  private sprite: PlayerSprite | undefined;
  private last: PlayerLook | undefined;
  private facing = Direction.S;

  private constructor(
    private readonly bodyFrames: BodyFrames,
    private readonly equipFrames: EquipFrames,
    private readonly store?: SpriteAtlasStore,
    private readonly size = PREVIEW_SIZE,
  ) {}

  /** Our store subscription, detached in {@link destroy}. */
  private onFramesLoaded?: FramesLoadListener;

  /** The residency pin on the currently previewed look (issue #625). */
  private pin?: () => void;

  /**
   * Build a preview, init its Pixi app, and mount its canvas into `mount`. Async
   * because `Application.init` is; the caller awaits it once the baked assets the
   * splash preloads are ready.
   *
   * The optional `store` supports the in-world wardrobe preview, whose look carries
   * equipment that now streams in lazily (ADR-0247): the preview requests its look's
   * atlases and re-renders when each arrives, so a dressed preview fills in rather than
   * showing the item undressed forever. The login splash preview passes no store - it
   * only ever draws the (essential, preloaded) naked body.
   */
  static async mount(
    mount: HTMLElement,
    bodyFrames: BodyFrames,
    equipFrames: EquipFrames,
    hueTable?: HueTable,
    store?: SpriteAtlasStore,
    size = PREVIEW_SIZE,
  ): Promise<SplashPreview> {
    const preview = new SplashPreview(bodyFrames, equipFrames, store, size);
    if (hueTable) configureUoHueBatch(hueTable);
    await preview.app.init({
      width: size,
      height: size,
      backgroundAlpha: 0,
      antialias: false,
      preference: 'webgl',
    });
    mount.appendChild(preview.app.canvas);
    // Share the render clock so the stand pose's occasional fidget plays, exactly as
    // an in-world idle sprite does. The character never walks here (moving: false).
    preview.app.ticker.add((ticker) => preview.sprite?.update(ticker.deltaMS, false));
    // Re-render when a lazily-streamed equipment atlas arrives, so a just-equipped item
    // fills into the preview once its atlas loads (ADR-0247).
    if (store) {
      const listener: FramesLoadListener = () => {
        if (preview.last && preview.app.stage) preview.rebuild(preview.last);
      };
      preview.onFramesLoaded = listener;
      store.addLoadListener(listener);
    }
    if (preview.last) preview.rebuild(preview.last);
    return preview;
  }

  /**
   * Re-render the preview for a freshly-chosen look. Cheap enough to call on every
   * selection change: it tears down the prior sprite and assembles a new stack from
   * the shared seam, so what the visitor sees is exactly what spawns.
   */
  update(look: PlayerLook): void {
    this.last = look;
    // Before init has resolved there is no stage yet; the mount path replays `last`.
    if (this.app.stage) this.rebuild(look);
  }

  private rebuild(look: PlayerLook): void {
    // Kick off any not-yet-loaded equipment atlas this look needs; the mount-time
    // listener re-renders once each arrives (ADR-0247). A no-op without a store.
    this.store?.requestLook(look);
    // Pin the previewed look against byte-budget eviction while it is on screen
    // (issue #625); the previous look's pin releases so it becomes evictable.
    // Retain-new-then-release-old, so bundles both looks share never dip to a zero
    // refcount (which could evict them mid-swap under budget pressure).
    const previous = this.pin;
    this.pin = this.store?.pinLook(look);
    previous?.();
    if (this.sprite) {
      this.app.stage.removeChild(this.sprite);
      this.sprite.destroy();
    }
    const layers = buildPlayerLayers(look, this.bodyFrames, this.equipFrames);
    // South is the spawn pose (facing into the hall), so the preview matches entry.
    const sprite = new PlayerSprite(layers, this.facing);
    this.app.stage.addChild(sprite);
    // Fit the whole assembled silhouette (every worn layer) into the canvas, centred
    // on its measured bounds rather than a fixed ground fraction - so the lower body
    // can never run off the bottom edge whatever the art's anchor or the outfit's
    // height. Measured at unit scale, which is why `getLocalBounds` is read here.
    const b = sprite.getLocalBounds();
    const fit = fitPreviewTransform(
      { x: b.x, y: b.y, width: b.width, height: b.height },
      this.size,
      Math.max(PREVIEW_PADDING, this.size * 0.08),
      5,
    );
    sprite.scale.set(fit.scale);
    sprite.position.set(fit.x, fit.y);
    this.sprite = sprite;
  }

  /** Rotate the preview through the eight UO facings without rebuilding its look. */
  rotate(steps: number): void {
    this.facing = rotatePreviewDirection(this.facing, steps);
    this.sprite?.setFacing(this.facing);
  }

  /** Tear down the Pixi app and remove its canvas (called when the visitor enters). */
  destroy(): void {
    this.pin?.();
    this.pin = undefined;
    if (this.onFramesLoaded) this.store?.removeLoadListener(this.onFramesLoaded);
    this.app.destroy(true, { children: true });
  }
}
